import { localize2WithPath } from 'vscode/vscode/vs/nls';
import { WorkbenchListFocusContextKey } from 'vscode/vscode/vs/platform/list/browser/listService';
import { IViewsService } from 'vscode/vscode/vs/workbench/services/views/common/viewsService.service';
import { searchStopIcon, searchRefreshIcon, searchCollapseAllIcon, searchExpandAllIcon, searchClearIcon, searchShowAsList, searchShowAsTree } from './searchIcons.js';
import { SearchContext } from 'vscode/vscode/vs/workbench/contrib/search/common/constants';
import { ISearchHistoryService } from 'vscode/vscode/vs/workbench/contrib/search/common/searchHistoryService.service';
import { FolderMatchWorkspaceRoot, Match, FolderMatch, FolderMatchNoRoot, SearchResult, FileMatch } from 'vscode/vscode/vs/workbench/contrib/search/browser/searchModel';
import { VIEW_ID } from 'vscode/vscode/vs/workbench/services/search/common/search';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { registerAction2, Action2, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { SearchStateKey, SearchUIState } from 'vscode/vscode/vs/workbench/contrib/search/common/search';
import { category, getSearchView } from 'vscode/vscode/vs/workbench/contrib/search/browser/searchActionsBase';

const _moduleId = "vs/workbench/contrib/search/browser/searchActionsTopBar";
registerAction2(class ClearSearchHistoryCommandAction extends Action2 {
    constructor() {
        super({
            id: "search.action.clearHistory" ,
            title: ( localize2WithPath(_moduleId, 0, "Clear Search History")),
            category,
            f1: true
        });
    }
    async run(accessor) {
        clearHistoryCommand(accessor);
    }
});
registerAction2(class CancelSearchAction extends Action2 {
    constructor() {
        super({
            id: "search.action.cancel" ,
            title: ( localize2WithPath(_moduleId, 1, "Cancel Search")),
            icon: searchStopIcon,
            category,
            f1: true,
            precondition: ( (( (SearchStateKey.isEqualTo(SearchUIState.Idle))).negate())),
            keybinding: {
                weight: 200 ,
                when: ( (ContextKeyExpr.and(SearchContext.SearchViewVisibleKey, WorkbenchListFocusContextKey))),
                primary: 9 ,
            },
            menu: [{
                    id: MenuId.ViewTitle,
                    group: 'navigation',
                    order: 0,
                    when: ( (ContextKeyExpr.and(
                         (ContextKeyExpr.equals('view', VIEW_ID)),
                         (SearchStateKey.isEqualTo(SearchUIState.SlowSearch))
                    ))),
                }]
        });
    }
    run(accessor) {
        return cancelSearch(accessor);
    }
});
registerAction2(class RefreshAction extends Action2 {
    constructor() {
        super({
            id: "search.action.refreshSearchResults" ,
            title: ( localize2WithPath(_moduleId, 2, "Refresh")),
            icon: searchRefreshIcon,
            precondition: SearchContext.ViewHasSearchPatternKey,
            category,
            f1: true,
            menu: [{
                    id: MenuId.ViewTitle,
                    group: 'navigation',
                    order: 0,
                    when: ( (ContextKeyExpr.and(
                         (ContextKeyExpr.equals('view', VIEW_ID)),
                         (( (SearchStateKey.isEqualTo(SearchUIState.SlowSearch))).negate())
                    ))),
                }]
        });
    }
    run(accessor, ...args) {
        return refreshSearch(accessor);
    }
});
registerAction2(class CollapseDeepestExpandedLevelAction extends Action2 {
    constructor() {
        super({
            id: "search.action.collapseSearchResults" ,
            title: ( localize2WithPath(_moduleId, 3, "Collapse All")),
            category,
            icon: searchCollapseAllIcon,
            f1: true,
            precondition: ( (ContextKeyExpr.and(SearchContext.HasSearchResults, SearchContext.ViewHasSomeCollapsibleKey))),
            menu: [{
                    id: MenuId.ViewTitle,
                    group: 'navigation',
                    order: 4,
                    when: ( (ContextKeyExpr.and(
                         (ContextKeyExpr.equals('view', VIEW_ID)),
                         (ContextKeyExpr.or(
                             (SearchContext.HasSearchResults.negate()),
                            SearchContext.ViewHasSomeCollapsibleKey
                        ))
                    ))),
                }]
        });
    }
    run(accessor, ...args) {
        return collapseDeepestExpandedLevel(accessor);
    }
});
registerAction2(class ExpandAllAction extends Action2 {
    constructor() {
        super({
            id: "search.action.expandSearchResults" ,
            title: ( localize2WithPath(_moduleId, 4, "Expand All")),
            category,
            icon: searchExpandAllIcon,
            f1: true,
            precondition: ( (ContextKeyExpr.and(
                SearchContext.HasSearchResults,
                 (SearchContext.ViewHasSomeCollapsibleKey.toNegated())
            ))),
            menu: [{
                    id: MenuId.ViewTitle,
                    group: 'navigation',
                    order: 4,
                    when: ( (ContextKeyExpr.and(
                         (ContextKeyExpr.equals('view', VIEW_ID)),
                        SearchContext.HasSearchResults,
                         (SearchContext.ViewHasSomeCollapsibleKey.toNegated())
                    ))),
                }]
        });
    }
    run(accessor, ...args) {
        return expandAll(accessor);
    }
});
registerAction2(class ClearSearchResultsAction extends Action2 {
    constructor() {
        super({
            id: "search.action.clearSearchResults" ,
            title: ( localize2WithPath(_moduleId, 5, "Clear Search Results")),
            category,
            icon: searchClearIcon,
            f1: true,
            precondition: ( (ContextKeyExpr.or(
                SearchContext.HasSearchResults,
                SearchContext.ViewHasSearchPatternKey,
                SearchContext.ViewHasReplacePatternKey,
                SearchContext.ViewHasFilePatternKey
            ))),
            menu: [{
                    id: MenuId.ViewTitle,
                    group: 'navigation',
                    order: 1,
                    when: ( (ContextKeyExpr.equals('view', VIEW_ID))),
                }]
        });
    }
    run(accessor, ...args) {
        return clearSearchResults(accessor);
    }
});
registerAction2(class ViewAsTreeAction extends Action2 {
    constructor() {
        super({
            id: "search.action.viewAsTree" ,
            title: ( localize2WithPath(_moduleId, 6, "View as Tree")),
            category,
            icon: searchShowAsList,
            f1: true,
            precondition: ( (ContextKeyExpr.and(
                SearchContext.HasSearchResults,
                 (SearchContext.InTreeViewKey.toNegated())
            ))),
            menu: [{
                    id: MenuId.ViewTitle,
                    group: 'navigation',
                    order: 2,
                    when: ( (ContextKeyExpr.and(
                         (ContextKeyExpr.equals('view', VIEW_ID)),
                         (SearchContext.InTreeViewKey.toNegated())
                    ))),
                }]
        });
    }
    run(accessor, ...args) {
        const searchView = getSearchView(accessor.get(IViewsService));
        if (searchView) {
            searchView.setTreeView(true);
        }
    }
});
registerAction2(class ViewAsListAction extends Action2 {
    constructor() {
        super({
            id: "search.action.viewAsList" ,
            title: ( localize2WithPath(_moduleId, 7, "View as List")),
            category,
            icon: searchShowAsTree,
            f1: true,
            precondition: ( (ContextKeyExpr.and(SearchContext.HasSearchResults, SearchContext.InTreeViewKey))),
            menu: [{
                    id: MenuId.ViewTitle,
                    group: 'navigation',
                    order: 2,
                    when: ( (ContextKeyExpr.and(
                         (ContextKeyExpr.equals('view', VIEW_ID)),
                        SearchContext.InTreeViewKey
                    ))),
                }]
        });
    }
    run(accessor, ...args) {
        const searchView = getSearchView(accessor.get(IViewsService));
        if (searchView) {
            searchView.setTreeView(false);
        }
    }
});
const clearHistoryCommand = accessor => {
    const searchHistoryService = accessor.get(ISearchHistoryService);
    searchHistoryService.clearHistory();
};
function expandAll(accessor) {
    const viewsService = accessor.get(IViewsService);
    const searchView = getSearchView(viewsService);
    if (searchView) {
        const viewer = searchView.getControl();
        viewer.expandAll();
    }
}
function clearSearchResults(accessor) {
    const viewsService = accessor.get(IViewsService);
    const searchView = getSearchView(viewsService);
    searchView?.clearSearchResults();
}
function cancelSearch(accessor) {
    const viewsService = accessor.get(IViewsService);
    const searchView = getSearchView(viewsService);
    searchView?.cancelSearch();
}
function refreshSearch(accessor) {
    const viewsService = accessor.get(IViewsService);
    const searchView = getSearchView(viewsService);
    searchView?.triggerQueryChange({ preserveFocus: false });
}
function collapseDeepestExpandedLevel(accessor) {
    const viewsService = accessor.get(IViewsService);
    const searchView = getSearchView(viewsService);
    if (searchView) {
        const viewer = searchView.getControl();
        const navigator = viewer.navigate();
        let node = navigator.first();
        let canCollapseFileMatchLevel = false;
        let canCollapseFirstLevel = false;
        if (node instanceof FolderMatchWorkspaceRoot || searchView.isTreeLayoutViewVisible) {
            while (node = navigator.next()) {
                if (node instanceof Match) {
                    canCollapseFileMatchLevel = true;
                    break;
                }
                if (searchView.isTreeLayoutViewVisible && !canCollapseFirstLevel) {
                    let nodeToTest = node;
                    if (node instanceof FolderMatch) {
                        const compressionStartNode = viewer.getCompressedTreeNode(node).element?.elements[0];
                        nodeToTest = (compressionStartNode && !(compressionStartNode instanceof Match)) ? compressionStartNode : node;
                    }
                    const immediateParent = nodeToTest.parent();
                    if (!(immediateParent instanceof FolderMatchWorkspaceRoot || immediateParent instanceof FolderMatchNoRoot || immediateParent instanceof SearchResult)) {
                        canCollapseFirstLevel = true;
                    }
                }
            }
        }
        if (canCollapseFileMatchLevel) {
            node = navigator.first();
            do {
                if (node instanceof FileMatch) {
                    viewer.collapse(node);
                }
            } while (node = navigator.next());
        }
        else if (canCollapseFirstLevel) {
            node = navigator.first();
            if (node) {
                do {
                    let nodeToTest = node;
                    if (node instanceof FolderMatch) {
                        const compressionStartNode = viewer.getCompressedTreeNode(node).element?.elements[0];
                        nodeToTest = (compressionStartNode && !(compressionStartNode instanceof Match)) ? compressionStartNode : node;
                    }
                    const immediateParent = nodeToTest.parent();
                    if (immediateParent instanceof FolderMatchWorkspaceRoot || immediateParent instanceof FolderMatchNoRoot) {
                        if (viewer.hasElement(node)) {
                            viewer.collapse(node, true);
                        }
                        else {
                            viewer.collapseAll();
                        }
                    }
                } while (node = navigator.next());
            }
        }
        else {
            viewer.collapseAll();
        }
        const firstFocusParent = viewer.getFocus()[0]?.parent();
        if (firstFocusParent && (firstFocusParent instanceof FolderMatch || firstFocusParent instanceof FileMatch) &&
            viewer.hasElement(firstFocusParent) && viewer.isCollapsed(firstFocusParent)) {
            viewer.domFocus();
            viewer.focusFirst();
            viewer.setSelection(viewer.getFocus());
        }
    }
}
